import { PACKET_END, PACKET_LENGTH, PACKET_START } from './constants';

export { PACKET_LENGTH } from './constants';

export const BOARD_CELLS = 200;
export const PREVIEW_CELLS = 16;
export const PREVIEW_COUNT = 4;
export interface DisplayPacket {
  raw: number[];
  protocolVersion: number;
  frameSequence: number;
  statusCode: number;
  eventCode: number;
  board: number[];
  previews: [number[], number[], number[]];
  holdPreview: number[];
  score: number;
  lines: number;
  level: number;
  combo: number;
  gameStarted: number;
  gameOver: number;
  paused: number;
  currentPiece: number;
  holdPiece: number;
  randomState: number;
  bagPosition: number;
  gravityTicks: number;
  lockTicks: number;
}

export function parsePacket(raw: number[]): DisplayPacket {
  if (!raw.every(Number.isInteger)) {
    throw new Error('엄랭 출력에는 정수만 올 수 있습니다.');
  }
  if (raw[0] !== PACKET_START || raw.at(-1) !== PACKET_END) {
    throw new Error('엄랭 출력 패킷 경계가 올바르지 않습니다.');
  }
  if (raw.length !== PACKET_LENGTH) {
    throw new Error(`엄랭 출력 길이 오류: ${raw.length}/${PACKET_LENGTH}`);
  }
  const values = raw.slice(1, -1);
  let cursor = 0;
  const take = (count: number) => values.slice(cursor, (cursor += count));
  const [protocolVersion, frameSequence, statusCode, eventCode] = take(4);
  const board = take(BOARD_CELLS);
  const preview1 = take(PREVIEW_CELLS);
  const preview2 = take(PREVIEW_CELLS);
  const preview3 = take(PREVIEW_CELLS);
  const holdPreview = take(PREVIEW_CELLS);
  const [
    score,
    lines,
    level,
    combo,
    gameStarted,
    gameOver,
    paused,
    currentPiece,
    holdPiece,
    randomState,
    bagPosition,
    gravityTicks,
    lockTicks,
  ] = take(13);

  assertExact('프로토콜 버전', protocolVersion, 1);
  assertRange('프레임 번호', frameSequence, 0, 2_147_483_647);
  assertOneOf('상태 코드', statusCode, [0, 2]);
  assertRange('이벤트 코드', eventCode, 0, 16);
  assertCells('화면', board, 8);
  assertCells('NEXT 1', preview1, 7);
  assertCells('NEXT 2', preview2, 7);
  assertCells('NEXT 3', preview3, 7);
  assertCells('HOLD', holdPreview, 7);
  assertFlag('시작 상태', gameStarted);
  assertFlag('게임 오버 상태', gameOver);
  assertFlag('일시정지 상태', paused);
  assertRange('현재 블록', currentPiece, 1, 7);
  assertRange('홀드 블록', holdPiece, 0, 7);
  assertRange('난수 상태', randomState, 0, 127);
  assertRange('7-bag 위치', bagPosition, 1, 7);
  assertRange('중력 틱', gravityTicks, 0, 16);
  assertRange('락 틱', lockTicks, 0, 9);
  assertRange('점수', score, 0, 999_999);
  assertRange('줄 수', lines, 0, 9_999);
  assertRange('레벨', level, 1, 99);
  assertRange('콤보', combo, 0, 99);
  return {
    raw,
    protocolVersion,
    frameSequence,
    statusCode,
    eventCode,
    board,
    previews: [preview1, preview2, preview3],
    holdPreview,
    score,
    lines,
    level,
    combo,
    gameStarted,
    gameOver,
    paused,
    currentPiece,
    holdPiece,
    randomState,
    bagPosition,
    gravityTicks,
    lockTicks,
  };
}

function assertCells(label: string, cells: readonly number[], maximum: number): void {
  if (cells.some((cell) => cell < 0 || cell > maximum)) {
    throw new Error(`${label} 셀 범위 오류`);
  }
}

function assertFlag(label: string, value: number): void {
  assertOneOf(label, value, [0, 1]);
}

function assertExact(label: string, value: number, expected: number): void {
  if (value !== expected) throw new Error(`${label} 오류: ${value}`);
}

function assertOneOf(
  label: string,
  value: number,
  expected: readonly number[],
): void {
  if (!expected.includes(value)) throw new Error(`${label} 오류: ${value}`);
}

function assertRange(
  label: string,
  value: number,
  minimum: number,
  maximum: number,
): void {
  if (value < minimum || value > maximum) {
    throw new Error(`${label} 범위 오류: ${value}`);
  }
}
